<script>
import {defineComponent, reactive} from "vue";
import DonationInfo from "@/javascript/views/components/landing/DonationInfo.vue";
import Icon from "../Icon.vue";
import tinycolor from "tinycolor2";

export default defineComponent({
  components: {Icon, DonationInfo},
  props: {
    /**
     * Campaign / Fund object
     */
    campaign: {
      type: Object,
      required: true
    },

    /**
     * Will show the short description if true.
     */
    showDescription: {
      type: Boolean,
      default: false
    },

    /**
     * The src of the campaign card image.
     */
    imageSrc: {
      type: String,
      default: 'https://d3dwclrxdfwggc.cloudfront.net/production/customer_assets/b18991dcec9b3140236d20e8836fac0be678e55f/original/placeholder-image.png'
    },
    /**
     * The alt text of the campaign card image.
     */
    imageAlt: {
      type: String,
      default: ''
    },
    /**
     * The position of the image in the campaign card.
     */
    imagePosition: {
      type: String,
      default: 'top',
      validator: (value) => {
        return ['top', 'bottom', 'right', 'left'].includes(value);
      }
    },
    /**
     * Background color of the card.
     * Can be any valid css color syntax.
     */
    backgroundColor: {
      type: String,
      default: 'white'
    },
    /**
     * Text color of card.
     */
    textColor: {
      type: String,
      default: '#000000'
    },
    /**
     * Primary color for donate bar, buttons, etc. Simplifying Props
     * And valid CSS color
     */
    primaryColor: {
      type: String,
      default: '#085E2DFF'
    },
    primaryColorDark: {
      type: String,
      default: '#05421a'
    },
    /**
     * Color used on top of primary color - ensure enough contrast!
     */
    primaryTextColor: {
      type: String,
      default: 'white'
    },
    neutralColor: {
      type: String,
      default: '#F3F2F2' // $color-section-bg
    },
    /**
     * Whether the card should be clickable.
     */
    clickable: {
      type: Boolean,
      default: false
    },

    /**
     * Variables for top Header, show/ hide, text and background colors
     */
    topHeader: {
      type: Boolean,
      default: false
    },
    headerText: {
      type: String,
      default: ""
    },
    headerColor: {
      type: String,
      default: '#000000'
    },
    headerTextColor: {
      type: String,
      default: "#FFFFFF"
    },
    headerIcon: {
      type: String,
      required: false
    },
    showLearnMoreButton: {
      type: Boolean,
      default: false
    },
    cardHeight: {
      type: String,
      default: 'medium',
      validator: (value) => {
        return ['small', 'medium', 'large'].includes(value);
      }
    },
  },
  setup(props) {
    props = reactive(props);
    return {};
  },
  data() {
    return {};
  },
  computed: {
    classes() {
      return {
        'landing-campaign-card': true,
        [`image-position--${this.imagePosition || 'top'}`]: true,
        'clickable': this.clickable,
        'landing-card--top-header': this.topHeader
      };
    },
    cssProps() {
      const defaultHeaderImage = 'https://d3dwclrxdfwggc.cloudfront.net/production/customer_assets/b18991dcec9b3140236d20e8836fac0be678e55f/original/placeholder-image.png';
      const defaultBannerUrl = `url('${defaultHeaderImage}')`;
      let bannerUrl = defaultBannerUrl;

      if (this.imageSrc) {
        bannerUrl = `url('${this.imageSrc}'), ${defaultBannerUrl}`;
      }
      // LQIP - Loads default banner first, then low res image, then high res
      if (this.campaign?.banner_image_data && this.campaign?.banner_image_data?.url) {
        bannerUrl = `url('${this.campaign.banner_image_data.url}'), url('${this.campaign?.banner_image_data.low_resolution_url}'), ${defaultBannerUrl}`;
      }

      // Ensure button color has enough contrast on white card background
      let buttonColor = tinycolor(this.primaryColor);
      if (!buttonColor.isValid()) {
        buttonColor = tinycolor('#333');
      }
      while (tinycolor.readability(buttonColor, '#fff') < 4.5 && buttonColor.getLuminance() > 0.05) {
        buttonColor = buttonColor.darken(5);
      }

      // Compute hover text color based on contrast against the button fill
      const hoverTextColor = tinycolor.readability(buttonColor, '#fff') >= 4.5 ? '#fff' : '#000';

      const cardHeightMap = {
        small: '180px',
        medium: '240px',
        large: '300px',
      };

      const cardImageWidthMap = {
        small: '35%',
        medium: '40%',
        large: '50%',
      };

      return {
        '--background-color': this.backgroundColor,
        '--text-color': this.textColor,
        '--top-bar-background-color': this.headerColor,
        '--top-bar-text-color': this.headerTextColor,
        '--banner-image-src': bannerUrl,
        '--button-color': buttonColor.toHexString(),
        '--button-hover-color': this.primaryColorDark,
        '--button-text-color': hoverTextColor,
        '--card-height': cardHeightMap[this.cardHeight] || cardHeightMap.medium,
        '--card-image-width': cardImageWidthMap[this.cardHeight] || cardImageWidthMap.medium
      };
    }
  },
  watch: {},
  mounted() {
    // console.log('Campaign data:', this.campaign);
  },
  methods: {}
});

</script>

<template>
  <div
    :class="classes"
    :style="cssProps"
  >
    <!-- Forces the DOM to preload low res banner image (LQIP) -->
    <img
      v-show="false"
      v-if="campaign?.banner_image_data?.low_resolution_url"
      :src="campaign?.banner_image_data.low_resolution_url"
      rel="preload"
    >
    <div
      v-if="topHeader"
      class="card-top-header w-full p-2 font-bold text-sm"
    >
      <div class="flex flex-row">
        <icon
          v-if="headerIcon"
          :icon="headerIcon"
          class="mr-2"
          :color="headerTextColor"
        />
        {{ headerText }}
      </div>
    </div>
    <div class="content-container">
      <div class="banner-image" />
      <div class="info-container">
        <div class="info-text">
          <h3 class="subtitle-medium">
            {{ campaign.name }}
          </h3>
          <p
            v-if="showDescription && campaign.short_description"
            class="body-1-regular description"
          >
            {{ campaign.short_description }}
          </p>
        </div>
        <div class="info-and-button-container">
          <!-- Show Progress Bar Only -->
          <donation-info
            v-if="campaign.donation_display_style === 'bar'"
            :curr-donations-total="campaign.all_time_contribution_total"
            :donation-goal="campaign.donation_target"
            :text-color="textColor"
            :donation-bar-base-color="neutralColor"
            :donation-bar-fill-color="primaryColorDark"
            :end-date="campaign.end_date"
            :show-bar="true"
            :show-amount="false"
          />

          <!-- Show Amount Only -->
          <donation-info
            v-else-if="campaign.donation_display_style === 'amount'"
            :curr-donations-total="campaign.all_time_contribution_total"
            :donation-goal="campaign.donation_target"
            :text-color="textColor"
            :end-date="campaign.end_date"
            :show-bar="false"
            :show-amount="true"
          />


          <div
            v-if="showLearnMoreButton"
            class="button-container flex-row"
          >
            <button class="btn btn-filled flex justify-center text-center">
              Give Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.landing-campaign-card {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--background-color);
  border-radius: $radius-rounded;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  &.clickable {
    cursor: pointer;
  }

  // Shared elements
  .content-container {
    height: 100%;
    display: flex;
  }

  .banner-image {
    min-height: 150px;
    width: 100%;
    background-image: var(--banner-image-src);
    background-size: cover;
    background-position: center;
  }

  .info-container {
    display: flex;
    text-align: left;
    color: var(--text-color);
    flex-direction: column;
    justify-content: space-between;

    .info-text {
      margin-top: $space-sm;
      min-height: 60px;

      h3 {
        margin-bottom: $space-xs;
        overflow-wrap: anywhere;
      }

      .description {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
        text-overflow: ellipsis;
        overflow: hidden;
        overflow-wrap: anywhere;
      }
    }

    .donation-info {
      margin-top: $space-lg;
    }
  }

  .info-and-button-container {
    display: flex;
    flex-direction: column;
  }

  button.btn-filled {
    background-color: transparent;
    color: var(--button-color);
    border: 2px solid var(--button-color);
    border-radius: $radius-rounded;
    outline: none;
    transition: all 0.2s ease;

    &:hover {
      background-color: var(--button-color) !important;
      color: var(--button-text-color) !important;
    }
  }

  // =====================
  // Image Left Layout
  // =====================
  &.image-position--left {
    .content-container {
      flex-direction: row;
      min-height: var(--card-height);
    }

    .banner-image {
      width: var(--card-image-width);
      height: auto;
      min-height: var(--card-height);
      flex-shrink: 0;
    }

    .info-container {
      flex: 1;
      padding: $space-md;

      .info-text {
        margin-top: 0;
      }

      .donation-info {
        margin-top: auto;
        margin-bottom: $space-md;
      }
    }

    .info-and-button-container {
      justify-content: flex-end;
    }

    .button-container {
      margin-top: $space-sm;
      display: flex;
      justify-content: flex-end;

      button {
        max-width: 160px;
        margin-right: $space-xs;
      }
    }

    .info-container .info-text .description {
      -webkit-line-clamp: 4;
    }

    // Mobile — stack vertically
    @media screen and (max-width: $breakpoint-max-sm) {
      .content-container {
        flex-direction: column;
        min-height: unset;
      }

      .banner-image {
        width: 100%;
        min-height: 180px;
      }

      .info-container {
        margin-left: 0;
        margin-top: 0;
      }

      .button-container {
        justify-content: stretch;

        button {
          max-width: 100%;
          width: 100%;
        }
      }
    }

    // Container — mobile preview in builder
    @container (min-width: 0px) and (max-width: 540px) {
      .content-container {
        flex-direction: column !important;
        min-height: unset !important;
      }

      .banner-image {
        width: 100% !important;
        min-height: 180px !important;
      }

      .info-container {
        margin-left: 0 !important;
        margin-top: 0 !important;
      }

      .button-container {
        justify-content: stretch !important;

        button {
          max-width: 100% !important;
          width: 100% !important;
        }
      }
    }
  }

  // =====================
  // Image Top Layout
  // =====================
  &.image-position--top {
    .content-container {
      flex-direction: column;
    }

    .banner-image {
      @apply h-2/6 w-full;
    }

    .info-container {
      flex: 1;
      padding: $space-md;

      .donation-info {
        margin-top: auto;
        margin-bottom: $space-md;
      }

      button {
        width: 100%;
      }
    }

    .button-container {
      margin-top: $space-sm;
    }
  }

  // =====================
  // Image Bottom Layout
  // =====================
  &.image-position--bottom {
    flex-direction: column-reverse;
  }

  // =====================
  // Image Right Layout
  // =====================
  &.image-position--right {
    .content-container {
      flex-direction: column;
    }

    .banner-image {
      @apply h-2/6 w-full;
    }

    .info-container {
      flex: 1;
      padding: $space-md;

      .donation-info {
        margin-top: auto;
        margin-bottom: $space-md;
      }

      button {
        width: 100%;
      }
    }

    .button-container {
      margin-top: $space-sm;
    }

    // Desktop — side by side
    @media screen and (min-width: $breakpoint-min-sm) {
      .content-container {
        flex-direction: row-reverse;
      }

      .banner-image {
        @apply w-1/3;
        min-height: 200px;
      }

      .info-container {
        @apply w-2/3;
        margin-left: $space-lg;
      }
    }

    // Container — mobile preview in builder
    @container (min-width: 0px) and (max-width: 540px) {
      .content-container {
        flex-direction: column !important;
      }

      .banner-image {
        width: 100% !important;
        min-height: unset !important;
      }

      .info-container {
        width: 100% !important;
        margin-left: 0 !important;
        margin-top: $space-md !important;
      }
    }

    .info-container .info-text .description {
      -webkit-line-clamp: 4;
    }
  }

  // =====================
  // Top Header Bar
  // =====================
  &.landing-card--top-header {
    .card-top-header {
      background-color: var(--top-bar-background-color);
      color: var(--top-bar-text-color);
      width: 100%;
    }
  }
}
</style>
